// Per-day Intake/Output chart (nursing-ops-2 #13): grouped bars (In vs Out, ml)
// with a net-balance line, over the IO entries currently in view. Follows the
// TprChart pattern — chart.js v3 requires explicit registration of every
// controller/element it uses or it silently renders nothing. Food (solid)
// entries are gram-denominated and deliberately excluded: this is a fluid
// chart (CONTEXT.md "IO entry" — grams never enter the fluid balance).
// NOTE: react-chartjs-2 is v3 here — the named `Chart` export is the chart.js
// class (re-exported from chart.js/auto), NOT a React component, and the
// `<Chart type="...">` generic-component form is a v4 API. Use the typed `Bar`
// component (same pattern TprChart uses `Line`); the net line rides as a
// per-dataset type:"line" mixed dataset.
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Legend,
  Tooltip,
} from "chart.js";
import { Box, Typography } from "@mui/material";
import { istDayKey } from "../../../utils/istDateTime";

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, PointElement, LineElement, LineController, Legend, Tooltip);

export default function IoChart({ entries = [] }) {
  const days = useMemo(() => {
    const byDay = new Map(); // "YYYY-MM-DD" -> { in, out }
    entries.forEach((e) => {
      // recorded_at is a UTC instant; bucketing on its raw date would file an
      // evening entry (after 18:30 IST) under the following day.
      const day = istDayKey(e.recorded_at) || null;
      // Food rows carry amount_ml=null (Number(null) is 0, which is finite —
      // so guard on null explicitly) and stay out of the fluid chart.
      if (!day || e.amount_ml == null || e.amount_ml === "") return;
      const ml = Number(e.amount_ml);
      if (!Number.isFinite(ml)) return;
      if (!byDay.has(day)) byDay.set(day, { in: 0, out: 0 });
      const d = byDay.get(day);
      if (e.type === "intake") d.in += ml;
      else if (e.type === "output") d.out += ml;
    });
    return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [entries]);

  if (days.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
        No I/O entries yet to chart.
      </Typography>
    );
  }

  const data = {
    labels: days.map(([day]) => day.slice(5)),
    datasets: [
      { type: "bar", label: "Intake (ml)", data: days.map(([, d]) => d.in), backgroundColor: "#2a78d6" },
      { type: "bar", label: "Output (ml)", data: days.map(([, d]) => d.out), backgroundColor: "#eda100" },
      {
        type: "line",
        label: "Net (ml)",
        data: days.map(([, d]) => d.in - d.out),
        borderColor: "#1baf7a",
        backgroundColor: "#1baf7a",
        tension: 0.2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    scales: { y: { title: { display: true, text: "ml" } } },
    plugins: { legend: { position: "bottom" } },
  };

  return (
    <Box sx={{ height: 240 }}>
      <Bar data={data} options={options} />
    </Box>
  );
}
